import { Link, TableCell, TableCellProps, Typography, TypographyProps, Stack, Skeleton } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../../../../../modules/localization/translations"
import { PropsWithChildren, ReactNode } from "react"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { FluentVariable } from "@fluent/bundle"

const useStyles = makeStyles()((theme) => ({
    blockTitle: {
        fontWeight: 500,
        fontStyle: "normal",
        fontSize: "1.875rem",
        lineHeight: "1.33",
        letterSpacing: 0,
        color: theme.palette.text.primary,
    },
    sectionTitle: {
        fontWeight: 500,
        fontSize: "1.25rem",
        lineHeight: "1.33",
        letterSpacing: "0.009rem",
        color: theme.palette.text.primary,
    },
    sectionSubTitle: {
        fontWeight: 500,
        fontStyle: "normal",
        fontSize: "1.0625rem",
        lineHeight: "1.5",
        letterSpacing: "0.009rem",
        color: theme.palette.text.primary,
    },
    sectionItemLabel: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: "1.5",
        letterSpacing: "0.009rem",
        color: theme.palette.text.secondary,
        "@media print": {
            fontSize: ".75rem",
            whiteSpace: "nowrap",
        },
    },
    sectionItemData: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: "1.5",
        letterSpacing: "0.009rem",
        color: theme.palette.text.primary,
        "@media print": {
            fontSize: ".75rem",
        },
    },
    tableLabel: {
        fontWeight: 600,
        fontStyle: "normal",
        fontSize: "1rem",
        lineHeight: "1.5",
        letterSpacing: "0.009rem",
        textTransform: "uppercase",
        color: tailwindPalette.slate[500],
    },
    sectionBlock: {
        padding: "20px",
        borderRadius: "16px",
    },
    titleDataContainer: {}, // in case we need to update container
    orderAmount: {
        fontWeight: 600,
        fontStyle: "normal",
        fontSize: "2.125rem",
        lineHeight: "1.24",
        letterSpacing: "0.016rem",
        color: theme.palette.text.primary,
    },
    sectionItemLink: {
        color: tailwindPalette.sky[700],
        overflowWrap: "anywhere",
        "@media print": {
            color: theme.palette.text.primary,
            fontSize: ".75rem",
        },
    },
}))

type WithChildrenAndClass = PropsWithChildren<{
    classNames?: string
}>

type WithTranslation = {
    translationKey?: TranslationKey
    translationVars?: Record<string, FluentVariable>
}

export const SectionTitle = ({
    children,
    translationKey,
    translationVars,
    classNames,
}: WithChildrenAndClass & WithTranslation) => {
    const { classes, cx } = useStyles()

    if (!translationKey) {
        return (
            <Typography variant="h6" className={cx(classes.sectionTitle, classNames)}>
                {children}
            </Typography>
        )
    }

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <Typography variant="h6" className={cx(classes.sectionTitle, classNames)}>
                {children}
            </Typography>
        </LocalizedStrict>
    )
}

export const SectionSubTitle = ({
    children,
    classNames,
    translationKey,
    translationVars,
}: WithChildrenAndClass & Partial<WithTranslation>) => {
    const { classes, cx } = useStyles()

    if (!translationKey) {
        return (
            <Typography variant="subtitle1" className={cx(classes.sectionSubTitle, classNames)}>
                {children}
            </Typography>
        )
    }

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <Typography variant="subtitle1" className={cx(classes.sectionSubTitle, classNames)}>
                {children}
            </Typography>
        </LocalizedStrict>
    )
}

export const BlockTitle = ({
    children,
    translationKey,
    classNames,
    translationVars,
}: WithChildrenAndClass & WithTranslation) => {
    const { classes, cx } = useStyles()

    if (!translationKey) {
        return (
            <Typography variant="h6" className={cx(classes.blockTitle, classNames)}>
                {children}
            </Typography>
        )
    }

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <Typography variant="h6" className={cx(classes.blockTitle, classNames)}>
                {children}
            </Typography>
        </LocalizedStrict>
    )
}

type SectionItemLabelProps = WithChildrenAndClass & Partial<WithTranslation>

export const SectionItemLabel = ({
    children,
    variant = "body1",
    translationKey,
    classNames,
    translationVars,
    ...rest
}: SectionItemLabelProps & TypographyProps) => {
    const { classes, cx } = useStyles()

    if (!translationKey) {
        return (
            <Typography variant={variant} className={cx(classes.sectionItemLabel, classNames)} {...rest}>
                {children}
            </Typography>
        )
    }

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <Typography variant={variant} className={cx(classes.sectionItemLabel, classNames)} {...rest}>
                {children}
            </Typography>
        </LocalizedStrict>
    )
}

type SectionItemLinkProps = PropsWithChildren<{
    href: string
}>

export const SectionItemLink = ({ href, children }: SectionItemLinkProps) => {
    const { classes } = useStyles()

    return (
        <Link href={href} underline="hover" align="right" className={classes.sectionItemLink}>
            {children}
        </Link>
    )
}

export const SectionItemData = ({
    children,
    classNames,
    translationKey,
    translationVars,
    variant = "body1",
    ...rest
}: WithChildrenAndClass & TypographyProps & WithTranslation) => {
    const { classes, cx } = useStyles()

    if (!translationKey) {
        return (
            <Typography variant={variant} className={cx(classes.sectionItemData, classNames)} {...rest}>
                {children}
            </Typography>
        )
    }

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <Typography variant={variant} className={cx(classes.sectionItemData, classNames)} {...rest}>
                {children}
            </Typography>
        </LocalizedStrict>
    )
}

export const SectionTitleDataContainer = ({ classNames, children }: WithChildrenAndClass) => {
    const { classes, cx } = useStyles()

    return (
        <Stack
            flexDirection="row"
            flexWrap="nowrap"
            justifyContent="space-between"
            gap="1rem"
            className={cx(classes.titleDataContainer, classNames)}
        >
            {children}
        </Stack>
    )
}

type TableLabelProps = TableCellProps & WithChildrenAndClass & WithTranslation

export const TableColumnLabel = ({
    children,
    translationKey,
    classNames,
    translationVars,
    ...rest
}: TableLabelProps) => {
    const { classes, cx } = useStyles()

    if (!translationKey) {
        return (
            <TableCell {...rest} className={cx(classes.tableLabel, classNames)}>
                {children}
            </TableCell>
        )
    }

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <TableCell {...rest} className={cx(classes.tableLabel, classNames)}>
                {children}
            </TableCell>
        </LocalizedStrict>
    )
}

type SectionBlockProps = WithChildrenAndClass & {
    gap?: number
    title: string
} & WithTranslation

export const SectionBlock = ({ classNames, gap = 2, title, children, translationKey }: SectionBlockProps) => {
    const { classes, cx } = useStyles()

    return (
        <Stack gap={gap} className={cx(classes.sectionBlock, classNames)}>
            <SectionTitle translationKey={translationKey}>{title}</SectionTitle>
            {children}
        </Stack>
    )
}

export const OrderAmount = ({ children, classNames }: WithChildrenAndClass) => {
    const { classes, cx } = useStyles()

    return (
        <Typography variant="h4" className={cx(classes.orderAmount, classNames)}>
            {children}
        </Typography>
    )
}

// Skeleton Components - reuse styles for consistent sizing

type SkeletonProps = {
    width?: number | string
    classNames?: string
}

export const SectionTitleSkeleton = ({ width = 140, classNames }: SkeletonProps) => {
    const { classes, cx } = useStyles()

    return (
        <Skeleton
            variant="text"
            width={width}
            className={cx(classes.sectionTitle, classNames)}
            animation="wave"
        />
    )
}

export const SectionItemLabelSkeleton = ({ width = 100, classNames }: SkeletonProps) => {
    const { classes, cx } = useStyles()

    return (
        <Skeleton
            variant="text"
            width={width}
            className={cx(classes.sectionItemLabel, classNames)}
            animation="wave"
        />
    )
}

export const SectionItemDataSkeleton = ({ width = 120, classNames }: SkeletonProps) => {
    const { classes, cx } = useStyles()

    return (
        <Skeleton
            variant="text"
            width={width}
            className={cx(classes.sectionItemData, classNames)}
            animation="wave"
        />
    )
}

export const SectionItemRowSkeleton = ({
    labelWidth = 100,
    dataWidth = 120,
    classNames,
}: {
    labelWidth?: number | string
    dataWidth?: number | string
    classNames?: string
}) => {
    return (
        <SectionTitleDataContainer classNames={classNames}>
            <SectionItemLabelSkeleton width={labelWidth} />
            <SectionItemDataSkeleton width={dataWidth} />
        </SectionTitleDataContainer>
    )
}

type SectionBlockSkeletonProps = {
    classNames?: string
    gap?: number
    titleWidth?: number
    children: ReactNode
}

export const SectionBlockSkeleton = ({ classNames, gap = 2, titleWidth = 140, children }: SectionBlockSkeletonProps) => {
    const { classes, cx } = useStyles()

    return (
        <Stack gap={gap} className={cx(classes.sectionBlock, classNames)}>
            <SectionTitleSkeleton width={titleWidth} />
            {children}
        </Stack>
    )
}

export const OrderAmountSkeleton = ({ width = 100, classNames }: SkeletonProps) => {
    const { classes, cx } = useStyles()

    return (
        <Skeleton
            variant="text"
            width={width}
            className={cx(classes.orderAmount, classNames)}
            animation="wave"
        />
    )
}

export const SectionSubTitleSkeleton = ({ width = 180, classNames }: SkeletonProps) => {
    const { classes, cx } = useStyles()

    return (
        <Skeleton
            variant="text"
            width={width}
            className={cx(classes.sectionSubTitle, classNames)}
            animation="wave"
        />
    )
}

export const BlockTitleSkeleton = ({ width = 160, classNames }: SkeletonProps) => {
    const { classes, cx } = useStyles()

    return (
        <Skeleton
            variant="text"
            width={width}
            className={cx(classes.blockTitle, classNames)}
            animation="wave"
        />
    )
}

export const TableColumnLabelSkeleton = ({ width = 100, classNames }: SkeletonProps) => {
    const { classes, cx } = useStyles()

    return (
        <Skeleton
            variant="text"
            width={width}
            className={cx(classes.tableLabel, classNames)}
            animation="wave"
        />
    )
}
